import { type NodeProps, Position } from '@xyflow/react';
import EditIcon from '@mui/icons-material/Edit';

import { type NodeItem, type SettingsNodeData, type ParentNode } from '@models/flows';
import { Connector } from '@utils/flows';

import { useToast } from '@hooks/useToast';
import { useNodeError } from '@hooks/useNodeError';

import {
  ConnectionPoint,
  DeleteButton,
  ErrorToolTip,
} from '@components/Flows/components/partials/index';
import { initialErrorNodeData } from '@features/chargerFlows/validation/validation-helpers';

import s from './SettingsNode.module.css';

type BlockProps = {
  nodeItem: NodeItem;
  onEdit: (code: string) => void;
};

const Block = ({ nodeItem: { label, id }, onEdit }: BlockProps) => (
  <div className={s.block}>
    <div className={s.lineWrapper}>
      <p className={s.label}>{label}</p>
    </div>
    <button type="button" className={s.editButton} onClick={() => onEdit(id)}>
      <EditIcon />
    </button>
  </div>
);

type nodeProps = NodeProps & {
  onEdit: (parentNode: ParentNode, childNode: NodeItem) => void;
  onChange: (parentNode: ParentNode, childNode: NodeItem) => void;
  onDelete: (nodeId: string) => void;
};

function SettingsNode({ id, type, data, onEdit, onDelete }: nodeProps) {
  const toast = useToast();
  const nodeData = data as unknown as SettingsNodeData;
  const nodeItems = nodeData?.items || [initialErrorNodeData];
  const nodeId = id;
  const parentNode: ParentNode = {
    id: nodeId,
    type,
    data: nodeData,
  };
  const errors = useNodeError(nodeId);

  const handleNodeItemEdit = (id: string) => {
    const editableItem = nodeItems.find((item) => item.id === id);

    if (!editableItem) {
      toast.error('Error item not found');
      return;
    }

    onEdit(parentNode, editableItem);
  };

  return (
    <div className={`${s.container} ${errors ? s.errorBorder : ''}`}>
      <h1 className={s.title}>Settings Node</h1>
      <ErrorToolTip isErrorVisible={Boolean(errors)} messages={errors?.messages} />
      <DeleteButton onDelete={() => onDelete(nodeId)} />
      <div className={s.blockList}>
        {nodeItems &&
          nodeItems.map((error: NodeItem, index: number) => (
            <Block key={index} nodeItem={error} onEdit={handleNodeItemEdit} />
          ))}
      </div>
      <ConnectionPoint type={Connector.Source} position={Position.Right} id="outgoing" />
    </div>
  );
}

export default SettingsNode;
